import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../util/Toast';
import {
  Users,
  Zap,
  Terminal,
  Search,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Bell,
  Globe,
  CheckCircle2,
  XCircle,
  ArrowUpRight,
  RefreshCw,
  Radio,
} from 'lucide-react';

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const TABS = [
  {
    key: 'users',
    label: 'User',
    icon: <Users className="w-3.5 h-3.5" />,
    accent: 'indigo',
  },
  {
    key: 'clicks',
    label: 'Click',
    icon: <Zap className="w-3.5 h-3.5" />,
    accent: 'purple',
  },
  {
    key: 'postbacks',
    label: 'Postback',
    icon: <Terminal className="w-3.5 h-3.5" />,
    accent: 'emerald',
  },
];

const ROWS_PER_PAGE = 10;

const AdminLogs = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('users');
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // Live ticker
  const [tickerIndex, setTickerIndex] = useState(0);

  // ==================== FETCH ====================
  const fetchLogs = useCallback(
    async (isRefresh = false) => {
      try {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        const baseUrl = process.env.REACT_APP_API_URL || '';
        const token = getCookie('adminToken') || getCookie('authToken');

        const response = await axios.get(
          `${baseUrl}/api/logs/${activeTab}?page=${page}&limit=${ROWS_PER_PAGE}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );

        if (response.data?.status) {
          setLogs(response.data.logs || []);
          setTotal(response.data.total || 0);
          setTickerIndex(0);
        } else {
          toast.error(response.data?.msg || 'Failed to load logs');
          setLogs([]);
          setTotal(0);
        }
      } catch (error) {
        console.error('Error fetching logs:', error);
        toast.error(error.response?.data?.msg || 'Failed to load logs');
        navigate('/emAdmin7/login');
        setLogs([]);
        setTotal(0);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [activeTab, page, navigate]
  );

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset page when tab changes
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  // ==================== LIVE TICKER ====================
  // Every 3s, cycle through the latest 3 logs as a "live feed"
  useEffect(() => {
    if (logs.length === 0) return;
    const maxIdx = Math.min(3, logs.length);
    const timer = setInterval(() => {
      setTickerIndex((prev) => (prev + 1) % maxIdx);
    }, 3000);
    return () => clearInterval(timer);
  }, [logs]);

  // ==================== FILTER (client-side within current page) ====================
  const filteredLogs = useMemo(() => {
    if (!searchQuery.trim()) return logs;
    const q = searchQuery.toLowerCase();
    return logs.filter(
      (log) =>
        (log.title || '').toLowerCase().includes(q) ||
        (log.ip || '').toLowerCase().includes(q) ||
        (log.status || '').toLowerCase().includes(q)
    );
  }, [logs, searchQuery]);

  // ==================== PAGINATION ====================
  const totalPages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));

  // ==================== HELPERS ====================
  const getTabMeta = (key) => {
    const t = TABS.find((x) => x.key === key);
    return t || TABS[0];
  };

  const currentTab = getTabMeta(activeTab);

  const accentClasses = (accent) => {
    switch (accent) {
      case 'indigo':
        return {
          bg: 'bg-indigo-500/10',
          text: 'text-indigo-400',
          border: 'border-indigo-500/20',
          glow: 'bg-indigo-500',
          activeBg: 'bg-indigo-500',
        };
      case 'purple':
        return {
          bg: 'bg-purple-500/10',
          text: 'text-purple-400',
          border: 'border-purple-500/20',
          glow: 'bg-purple-500',
          activeBg: 'bg-purple-500',
        };
      case 'emerald':
      default:
        return {
          bg: 'bg-emerald-500/10',
          text: 'text-emerald-400',
          border: 'border-emerald-500/20',
          glow: 'bg-emerald-500',
          activeBg: 'bg-emerald-500',
        };
    }
  };

  const accent = accentClasses(currentTab.accent);
  const latestLog = logs.length > 0 ? logs[tickerIndex % Math.min(3, logs.length)] : null;

  return (
    <div className="min-h-screen bg-zinc-950 font-sans text-zinc-100 w-full pt-4 lg:pt-20 lg:pl-72 transition-all duration-500">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(20px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-spring-in { animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

        @keyframes slideFade {
          0% { opacity: 0; transform: translateX(20px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        .animate-slide-fade { animation: slideFade 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

        @keyframes pulseRing {
          0% { box-shadow: 0 0 0 0 rgba(16,185,129, 0.4); }
          70% { box-shadow: 0 0 0 8px rgba(16,185,129, 0); }
          100% { box-shadow: 0 0 0 0 rgba(16,185,129, 0); }
        }
        .pulse-ring { animation: pulseRing 2s infinite; }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .shimmer-bg {
          background: linear-gradient(90deg, rgba(39,39,42,0.6) 25%, rgba(63,63,70,0.6) 50%, rgba(39,39,42,0.6) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite;
        }

        .micro-interaction {
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .micro-interaction:hover { transform: translateY(-2px); }
        .micro-interaction:active { transform: translateY(0px) scale(0.98); }

        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); border-radius: 8px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 8px; }
      `}</style>

      <div className="p-4 sm:p-8 space-y-6 max-w-[1400px] mx-auto">

        {/* ==================== HEADER ==================== */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-spring-in">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-100">
              Command Center
            </h2>
            <p className="text-zinc-500 text-xs sm:text-sm font-medium mt-1">
              Real-time system transparency
            </p>
          </div>

          <button
            onClick={() => fetchLogs(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900 border border-zinc-800 hover:border-emerald-500/50 text-zinc-200 rounded-xl text-xs font-black uppercase tracking-widest transition-all micro-interaction disabled:opacity-50"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
            />
            {refreshing ? 'Syncing...' : 'Refresh'}
          </button>
        </div>

        {/* ==================== LIVE TICKER (UNIQUE HIGHLIGHT) ==================== */}
        <div className="relative overflow-hidden rounded-[2rem] border border-zinc-800 bg-gradient-to-r from-zinc-900 via-zinc-900/95 to-zinc-900 animate-spring-in">
          {/* Ambient glow per accent */}
          <div
            className={`absolute top-0 right-0 w-64 h-64 -mr-16 -mt-16 rounded-full blur-3xl opacity-20 ${accent.glow}`}
          />

          <div className="relative z-10 flex items-center gap-4 p-5 sm:p-6">
            {/* Live pulsing dot */}
            <div className="relative shrink-0">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center ${accent.bg} ${accent.text} border ${accent.border}`}
              >
                <Radio className="w-5 h-5" />
              </div>
              <div
                className={`absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-zinc-900 pulse-ring`}
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400">
                  Live Feed
                </span>
                <span className="text-[10px] text-zinc-600">•</span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Latest {Math.min(3, logs.length || 0)} events
                </span>
              </div>

              {loading ? (
                <div className="h-5 w-64 bg-zinc-800 rounded shimmer-bg" />
              ) : latestLog ? (
                <div
                  key={tickerIndex}
                  className="animate-slide-fade flex items-center gap-3 min-w-0"
                >
                  <p className="text-sm sm:text-base font-bold text-zinc-100 truncate">
                    {latestLog.title}
                  </p>
                  <span className="hidden sm:inline text-[10px] font-mono font-bold text-zinc-500 shrink-0">
                    {latestLog.time}
                  </span>
                </div>
              ) : (
                <p className="text-sm font-bold text-zinc-500 italic">
                  No live events yet
                </p>
              )}
            </div>

            {/* Ticker dots */}
            {logs.length > 0 && (
              <div className="hidden sm:flex items-center gap-1.5 shrink-0">
                {[...Array(Math.min(3, logs.length))].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setTickerIndex(i)}
                    aria-label={`Show event ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === tickerIndex % Math.min(3, logs.length)
                        ? `w-6 ${accent.activeBg}`
                        : 'w-1.5 bg-zinc-700 hover:bg-zinc-600'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Progress bar */}
          <div className="h-0.5 bg-zinc-800/60 overflow-hidden">
            <div
              key={tickerIndex}
              className={`h-full ${accent.glow} opacity-60`}
              style={{
                animation: 'progressBar 3s linear',
              }}
            />
          </div>
          <style>{`
            @keyframes progressBar {
              from { width: 0%; }
              to { width: 100%; }
            }
          `}</style>
        </div>

        {/* ==================== TABS + SEARCH ==================== */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 animate-spring-in">
          {/* Tabs */}
          <div className="flex bg-zinc-900/90 p-1 rounded-2xl border border-zinc-800 shrink-0">
            {TABS.map((tab) => {
              const tAccent = accentClasses(tab.accent);
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${
                    isActive
                      ? `${tAccent.activeBg} text-zinc-950 shadow-lg`
                      : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative flex-1 group">
            <Search className="w-3.5 h-3.5 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 group-focus-within:text-emerald-400 transition-colors" />
            <input
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-xs font-bold outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/15 transition-all text-zinc-100 placeholder:text-zinc-600"
            />
          </div>
        </div>

        {/* ==================== LOGS CARD ==================== */}
        <div className="bg-zinc-900/90 border border-zinc-800 rounded-[2rem] shadow-xl overflow-hidden animate-spring-in flex flex-col">

          {/* Card header */}
          <div className="p-5 sm:p-6 border-b border-zinc-800 flex items-center justify-between bg-zinc-950/40 shrink-0">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-2xl flex items-center justify-center ${accent.bg} ${accent.text} border ${accent.border}`}
              >
                <Bell className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm sm:text-base font-black text-zinc-100 capitalize">
                  {currentTab.label} Activity
                </h3>
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-0.5">
                  {total.toLocaleString()} total records
                </p>
              </div>
            </div>
          </div>

          {/* Card body — scrollable */}
          <div className="overflow-y-auto custom-scrollbar max-h-[60vh]">
            {loading ? (
              <div className="p-4 sm:p-6 space-y-3">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4 p-4 rounded-2xl bg-zinc-950/40"
                  >
                    <div className="w-10 h-10 rounded-xl bg-zinc-800 shimmer-bg" />
                    <div className="flex-1 space-y-2">
                      <div className="w-2/3 h-3 bg-zinc-800 rounded shimmer-bg" />
                      <div className="w-1/3 h-2 bg-zinc-800 rounded shimmer-bg" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="py-20 flex flex-col items-center justify-center text-center px-6">
                <CircleAlert className="w-12 h-12 text-zinc-700 mb-4" />
                <p className="text-sm font-bold text-zinc-500 italic">
                  {searchQuery
                    ? 'No logs match your search.'
                    : 'No activity logs available.'}
                </p>
              </div>
            ) : (
              <div className="p-4 sm:p-6 space-y-3">
                {filteredLogs.map((log, idx) => {
                  const isSuccess =
                    String(log.status || '').toUpperCase() === 'SUCCESS';
                  const hasStatus = 'status' in log;

                  return (
                    <div
                      key={idx}
                      style={{ animationDelay: `${idx * 30}ms` }}
                      className="group flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-2xl bg-zinc-950/40 border border-zinc-800/60 hover:border-zinc-700 hover:bg-zinc-950/70 transition-all animate-spring-in"
                    >
                      {/* Icon */}
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-transform duration-300 group-hover:scale-110 ${accent.bg} ${accent.text} ${accent.border}`}
                      >
                        {currentTab.icon}
                      </div>

                      {/* Title + meta */}
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-black text-zinc-100 truncate group-hover:text-white transition-colors">
                            {log.title}
                          </p>
                          {hasStatus && (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black uppercase border ${
                                isSuccess
                                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                  : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                              }`}
                            >
                              {isSuccess ? (
                                <CheckCircle2 className="w-2.5 h-2.5" />
                              ) : (
                                <XCircle className="w-2.5 h-2.5" />
                              )}
                              {log.status}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                          <span>{log.time}</span>
                          {log.ip && (
                            <>
                              <span className="text-zinc-700">•</span>
                              <span className="flex items-center gap-1 font-mono">
                                <Globe className="w-3 h-3" />
                                {log.ip}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Right arrow for desktop */}
                      <button
                        className="hidden md:flex p-2 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-500 hover:text-emerald-400 hover:border-emerald-500/40 transition-all micro-interaction shrink-0"
                        title="View details"
                      >
                        <ArrowUpRight className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ==================== FOOTER / PAGINATION ==================== */}
          <div className="p-4 sm:p-5 bg-zinc-950/60 border-t border-zinc-800 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
            <p className="hidden sm:block text-[10px] font-black text-zinc-500 uppercase tracking-widest">
              System Health:{' '}
              <span className="text-emerald-400 ml-1">Optimal</span>
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all micro-interaction"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div className="px-4 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl">
                <span className="text-[10px] font-black uppercase tracking-widest text-zinc-300">
                  Page {page} / {totalPages}
                </span>
              </div>

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all micro-interaction"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminLogs;